import { FormFillerApi } from 'shared-utils/components/FormFiller/FormFillerProvider';
import { client } from '../api';
import { FormAnswers, FormRole } from 'shared-utils/types/formFiller';

/**
 * Instead of requiring the exact same API structure, for partner + patient portal, we'll dependency
 * inject the relevant API endpoints this way
 */
export const createFormFillerApi = (roleOverride?: FormRole, onComplete?: () => Promise<void>): FormFillerApi => {
  const roleOverrideParam = roleOverride ? `?role_override=${roleOverride}` : '';
  return {
    setFormAnswers: async (args: { token: string; answers: FormAnswers; signatureImageS3Key: string | null }) => {
      const response = await client.post<{ status: string }>(
        `/forms/${args.token}/set-form-answers${roleOverrideParam}`,
        {
          answers: args.answers,
          signature_s3_key: args.signatureImageS3Key,
        },
      );
      return response.data;
    },
    uploadSignature: async (file: File, token: string) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await client.post<{ s3_key: string; preview_url: string }>(
        `/forms/${token}/upload-signature${roleOverrideParam}`,
        formData,
      );
      return {
        s3Key: response.data.s3_key,
        previewUrl: response.data.preview_url,
      };
    },
    completeForm: async (args: {
      token: string;
      answers: FormAnswers;
      signatureImageS3Key: string | null;
      providerAttestedPatientConsent?: boolean;
    }) => {
      const response = await client.post<{ status: string }>(`/forms/${args.token}/complete-form${roleOverrideParam}`, {
        answers: args.answers,
        signature_s3_key: args.signatureImageS3Key,
        ...(args.providerAttestedPatientConsent !== undefined && {
          provider_attested_patient_consent: args.providerAttestedPatientConsent,
        }),
      });
      if (response.data.status === 'ok' && onComplete) {
        await onComplete();
      }
      return response.data as { status: string };
    },
    getFormFillerTemplateJson: async (token: string) => {
      const response = await client.get(`/forms/${token}/get-form-filler-template${roleOverrideParam}`);
      return response.data;
    },
    getFormFillerAnswers: async (token: string) => {
      const response = await client.get(`/forms/${token}/get-form-filler-answers${roleOverrideParam}`);
      return response.data;
    },
    getFormFillerBasePdfUrl: (token: string) => {
      return `${client.defaults.baseURL}/forms/${token}/base-pdf${roleOverrideParam}`;
    },
    getBasePdfDownloadUrl: (token: string) => {
      const attachmentParam = roleOverrideParam ? '&attachment=true' : '?attachment=true';
      return `${client.defaults.baseURL}/forms/${token}/base-pdf${roleOverrideParam}${attachmentParam}`;
    },
    getCompletedDocumentUrl: (token: string, attachment: boolean) => {
      const attachmentParam = attachment ? (roleOverrideParam ? '&attachment=true' : '?attachment=true') : '';
      return `${client.defaults.baseURL}/forms/${token}/completed-document-pdf${roleOverrideParam}${attachmentParam}`;
    },
    getFormInfo: async (token: string) => {
      const response = await client.get(`/forms/${token}/get-form-info${roleOverrideParam}`);
      return response.data;
    },
  };
};
